import React, { useEffect, useRef, useState } from "react";
import "../../userscreen/style.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  message,
  Input,
  Button,
  Popconfirm,
  Select,
  Modal,
} from "antd";
import { Container, Row, Col } from "react-bootstrap";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { GET, POST, PUT } from "../../../utils/apirequest";
import {
  DISTRICT_CODE_MAP,
  districts,
  countryHasZones,
  zonesForCountry,
} from "../../../config/zones";
import axios from "../../../config/axiosInstance";
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  activeCountries,
  getCountry,
  normalizeCountry,
  splitDigitsPhone,
} from "../../../config/countries";
import { loadEpaperConfig, useEpaperConfig } from "../../../config/epaperConfig";
import { REGIONS, findRegion, regionForCountry } from "../../../config/regions";
import { useSelector } from "react-redux";
import { checkPhone } from "../../../utils/phoneValidation";

const EPAPER_AGENT_ROLE = 5;
const EPAPER_USERS_ACCESS = ["6", "0"];
// Country dropdown value for agents outside the E-Paper Settings countries.
// Saved as the agent's country, with "Others" as the zone (backend rule).
const OTHER_COUNTRY = "Others";
const isOtherCountry = (value: unknown) =>
  typeof value === "string" && /^others?$/i.test(value.trim());
// Region for a country: the region set on it in E-Paper Settings, "Others"
// for the Others country, or undefined when the country has none.
const regionForAgentCountry = (country: string): string | undefined =>
  country === OTHER_COUNTRY ? OTHER_COUNTRY : regionForCountry(country)?.code;
// Region shown when editing: the country's configured region (Region is
// read-only and follows the country). Only when the country has none is the
// saved up_users.region kept, if it is an E-Paper Settings region or "Others".
const regionFromSaved = (region: unknown, country: string): string | undefined => {
  const configured = regionForAgentCountry(country);
  if (configured) return configured;
  if (isOtherCountry(region)) return OTHER_COUNTRY;
  return findRegion(typeof region === "string" ? region : "")?.code;
};
const OTHER_DISTRICT_VALUE = "Others";
// Field feedback without the green success tick; validating and error
// icons and messages are unchanged.
const FEEDBACK_NO_SUCCESS_ICON = { icons: () => ({ success: false }) };
// Phone country dropdown value that lets the calling code be typed by hand.
const OTHER_PHONE_COUNTRY = "OTHER";

// "91", "+91", "+ 9 1" → "+91" (at most 4 digits); "" when empty.
const normalizeDialCode = (value: unknown): string => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 4);
  return digits ? `+${digits}` : "";
};

// Agent names: letters, with single spaces between words ("John Doe").
const USERNAME_PATTERN = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;
// Trims and collapses repeated spaces: "  John   Doe " → "John Doe".
const cleanName = (value: unknown): string =>
  String(value ?? "").trim().replace(/\s+/g, " ");
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Form label in the CMS style, with a red * for required fields.
const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="newsFormLabels">
    {children}
    {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
  </label>
);

// Checks the local number against the calling code, naming the selected
// country in messages ("India (+91)") or just the code for Others. A code of
// an active E-Paper Settings country (even typed under Others) is accepted
// when libphonenumber doesn't know it (custom code).
const validatePhone = (phoneCountry?: string, dialCode?: string, local?: string) => {
  const isCountry = !!phoneCountry && phoneCountry !== OTHER_PHONE_COUNTRY;
  const where = isCountry ? `${getCountry(phoneCountry).name} (${dialCode})` : dialCode || "";
  const configured = !!dialCode && activeCountries().some((c) => c.dialCode === dialCode);
  return checkPhone(dialCode, local, where, isCountry ? phoneCountry : undefined, configured);
};

// Phone country (an active E-Paper Settings country) for a calling code,
// preferring `preferred` when it shares the code. Codes no active country
// uses fall under "Others" so they stay editable.
const phoneCountryFor = (dialCode?: string, preferred?: string): string => {
  if (!dialCode) {
    return activeCountries().some((c) => c.code === preferred)
      ? (preferred as string)
      : OTHER_PHONE_COUNTRY;
  }
  const matches = activeCountries().filter((c) => c.dialCode === dialCode);
  return (
    matches.find((c) => c.code === preferred)?.code ||
    matches[0]?.code ||
    OTHER_PHONE_COUNTRY
  );
};

function CreateAgent() {
  const navigate = useNavigate();
  let { id } = useParams();
  const [form] = Form.useForm();
  // Country lists the E-Paper Settings countries plus "Others"; it sets the
  // phone code, the zone list and the Region. Region (saved in
  // up_users.region) is read-only, filled in from the country's region in
  // E-Paper Settings → Countries ("Others" for Others). Countries without zones
  // use the country's name as the zone, and "Others" uses "Others".
  useEpaperConfig();
  const agentCountry: string = Form.useWatch("country", form) || DEFAULT_COUNTRY;
  const agentRegion: string | undefined = Form.useWatch("region", form);
  const currentZone: string | undefined = Form.useWatch("zone", form);
  const hasZones = agentCountry !== OTHER_COUNTRY && countryHasZones(agentCountry);
  // Active countries, plus the current one even if since disabled, then "Others".
  const countryOptions = [
    ...COUNTRIES.filter((c) => c.isActive || c.code === agentCountry).map((c) => ({
      value: c.code,
      label: `${c.name}${c.isActive ? "" : " (inactive)"}`,
    })),
    { value: OTHER_COUNTRY, label: "Others" },
  ];
  // Active regions, plus the current one even if since disabled, then "Others".
  const regionOptions = [
    ...REGIONS.filter((r) => r.isActive || r.code === agentRegion).map((r) => ({
      value: r.code,
      label: `${r.name}${r.isActive ? "" : " (inactive)"}`,
    })),
    { value: OTHER_COUNTRY, label: "Others" },
  ];
  const zoneOptions = zonesForCountry(agentCountry, { activeOnly: false })
    .filter((z) => z.isActive || z.name === currentZone)
    .map((z) => ({
      value: z.name,
      label: `${z.name} (${z.code})${z.isActive ? "" : " (inactive)"}`,
    }));
  // Zone value fixed by the country when it has no zone list; undefined when
  // a zone has to be picked.
  const fixedZoneFor = (code: string): string | undefined =>
    code === OTHER_COUNTRY
      ? OTHER_COUNTRY
      : !countryHasZones(code)
        ? getCountry(code).name
        : undefined;
  const handleCountryChange = (code: string) => {
    // Region follows the country's configured region (read-only); the zone list is the new country's, and a picked zone is
    // kept only when the new country also has it.
    const fixed = fixedZoneFor(code);
    const zone = form.getFieldValue("zone");
    form.setFieldsValue({ region: regionForAgentCountry(code) });
    if (fixed) {
      form.setFieldsValue({ zone: fixed });
    } else if (zone && !zonesForCountry(code).some((z) => z.name === zone)) {
      form.setFieldsValue({ zone: undefined });
    }
    // No calling code to follow for Others; the phone country is left as it is.
    if (code === OTHER_COUNTRY) return;
    // Only a new agent with no number typed yet has the calling code follow
    // the country (it can still be changed in the phone selector). Editing
    // keeps the agent's saved phone and code.
    if (id !== "create" || form.getFieldValue("phone_number")) return;
    const dialCode = getCountry(code).dialCode;
    form.setFieldsValue({
      countryCode: dialCode,
      phoneCountry: phoneCountryFor(dialCode, code),
    });
  };
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState({}) as any;
  const [passwordValue, setPasswordValue] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState<string | undefined>(
    undefined
  );
  const currentUser = useSelector((state: any) => state?.User?.user);

  type CheckStatus = "idle" | "checking" | "free" | "taken";
  const [emailCheck, setEmailCheck] = useState<{
    status: CheckStatus;
    message: string;
  }>({ status: "idle", message: "" });
  const [phoneCheck, setPhoneCheck] = useState<{
    status: CheckStatus;
    message: string;
  }>({ status: "idle", message: "" });

  const emailValue = Form.useWatch("email", form) as string | undefined;
  const phoneValue = Form.useWatch("phone_number", form) as string | undefined;
  const dialCodeValue = Form.useWatch("countryCode", form) as string | undefined;
  const phoneCountryValue = Form.useWatch("phoneCountry", form) as string | undefined;
  const isOtherPhoneCountry = phoneCountryValue === OTHER_PHONE_COUNTRY;
  // "(+91) India" for each active E-Paper Settings country, then "Others"
  // (shown with the typed code once there is one).
  const phoneCountryOptions = [
    ...activeCountries().map((c) => ({
      value: c.code,
      label: `(${c.dialCode}) ${c.name}`,
    })),
    {
      value: OTHER_PHONE_COUNTRY,
      label: isOtherPhoneCountry && dialCodeValue ? `(${dialCodeValue}) Others` : "Others",
    },
  ];
  const handlePhoneCountryChange = (value: string) => {
    form.setFieldsValue({
      countryCode:
        value === OTHER_PHONE_COUNTRY ? "" : getCountry(value).dialCode,
    });
    // setFieldsValue doesn't re-run dependent rules; re-check a typed number
    // against the new code.
    if (form.getFieldValue("phone_number")) {
      form.validateFields(["countryCode", "phone_number"]).catch(() => {});
    }
  };
  // Phone as loaded for editing. up_users.phone_number is a bigint, so the
  // phone is stored as digits only: dial code + number ("971501234567").
  // Unchanged phones are sent back exactly as stored, so legacy numbers
  // saved without a country code are not rewritten by an unrelated edit.
  const originalPhoneRef = useRef<{ raw: string; dialCode: string; number: string } | null>(null);
  // Username as loaded for editing; an unchanged legacy name is not re-checked
  // against the alphanumeric rule.
  const originalNameRef = useRef<string | null>(null);
  const fullPhone = (dialCode?: string, number?: string): string => {
    const local = String(number ?? "").trim();
    if (!local) return "";
    const original = originalPhoneRef.current;
    if (original && dialCode === original.dialCode && local === original.number) {
      return original.raw;
    }
    return `${String(dialCode || "").replace(/\D/g, "")}${local}`;
  };
  const isOriginalPhone = (dialCode?: string, number?: string): boolean => {
    const original = originalPhoneRef.current;
    return !!original && dialCode === original.dialCode && String(number ?? "").trim() === original.number;
  };
  const phoneLocal = String(phoneValue ?? "").trim();
  const phoneResult = validatePhone(phoneCountryValue, dialCodeValue, phoneLocal);
  const emailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phoneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live email duplicate check. Debounced 400ms after the user stops typing.
  // Soft-deleted matches are already filtered server-side, so any "exists"
  // here is a real, live conflict.
  useEffect(() => {
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    const raw = (emailValue || "").trim().toLowerCase();
    if (!raw) {
      setEmailCheck({ status: "idle", message: "" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      setEmailCheck({ status: "idle", message: "" });
      return;
    }
    setEmailCheck({ status: "checking", message: "Checking…" });
    emailTimerRef.current = setTimeout(async () => {
      try {
        const url = `${API.BASE_URL}${API.IDENTITY_CHECK}?email=${encodeURIComponent(raw)}`;
        const res = await axios.get(url);
        const data = res?.data || {};
        const hit = data.up || data.epaper;
        const editingSameUser =
          id !== "create" && data.up && String(data.up.id) === String(id);
        if (hit && !editingSameUser) {
          const who =
            data.up?.username
              ? `agent "${data.up.username}"`
              : data.epaper?.full_name
                ? `subscriber "${data.epaper.full_name}"`
                : "another user";
          setEmailCheck({
            status: "taken",
            message: `This email is already used by ${who}.`,
          });
        } else {
          setEmailCheck({ status: "free", message: "Available" });
        }
      } catch {
        setEmailCheck({ status: "idle", message: "" });
      }
    }, 400);
    return () => {
      if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    };
  }, [emailValue, id]);

  // Live phone duplicate check. Same debounce/treatment as email.
  useEffect(() => {
    if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current);
    const local = phoneValue ? String(phoneValue).trim() : "";
    const raw = fullPhone(dialCodeValue, local);
    if (!raw) {
      setPhoneCheck({ status: "idle", message: "" });
      return;
    }
    // Only complete, valid numbers (or the agent's unchanged own number)
    // are looked up.
    if (!validatePhone(phoneCountryValue, dialCodeValue, local).valid && !isOriginalPhone(dialCodeValue, local)) {
      setPhoneCheck({ status: "idle", message: "" });
      return;
    }
    setPhoneCheck({ status: "checking", message: "Checking…" });
    phoneTimerRef.current = setTimeout(async () => {
      try {
        const url = `${API.BASE_URL}${API.IDENTITY_CHECK}?phone=${encodeURIComponent(raw)}`;
        const res = await axios.get(url);
        const data = res?.data || {};
        const hit = data.up || data.epaper;
        const editingSameUser =
          id !== "create" && data.up && String(data.up.id) === String(id);
        if (hit && !editingSameUser) {
          const who =
            data.up?.username
              ? `agent "${data.up.username}"`
              : data.epaper?.full_name
                ? `subscriber "${data.epaper.full_name}"`
                : "another user";
          setPhoneCheck({
            status: "taken",
            message: `This phone is already used by ${who}.`,
          });
        } else {
          setPhoneCheck({ status: "free", message: "Available" });
        }
      } catch {
        setPhoneCheck({ status: "idle", message: "" });
      }
    }, 400);
    return () => {
      if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current);
    };
  }, [phoneValue, dialCodeValue, phoneCountryValue, id]);

  useEffect(() => {
    if (id !== "create") {
      loadData(id);
    } else {
      // Wait for E-Paper Settings so the phone country matches its list.
      loadEpaperConfig().then(() => {
        const dialCode = getCountry(DEFAULT_COUNTRY).dialCode;
        form.setFieldsValue({
          country: DEFAULT_COUNTRY,
          region: regionForAgentCountry(DEFAULT_COUNTRY),
          zone: fixedZoneFor(DEFAULT_COUNTRY),
          countryCode: dialCode,
          phoneCountry: phoneCountryFor(dialCode, DEFAULT_COUNTRY),
        });
      });
    }
  }, []);

  const loadData = async (id: any) => {
    setLoading2(true);
    let url = API.USERS + `/${id}?populate=role`;
    try {
      const [response]: any[] = await Promise.all([
        GET(url, null, null),
        loadEpaperConfig(),
      ]);
      if (response?.id) {
        // Agents saved before the country field existed have only region
        // "india"/"gulf": India maps to IN when it is configured (otherwise
        // Others); the rest default to UAE.
        const indiaConfigured = activeCountries().some((c) => c.code === "IN");
        const country = isOtherCountry(response?.country)
          ? OTHER_COUNTRY
          : normalizeCountry(response?.country) ||
            (response?.region === "india"
              ? indiaConfigured ? "IN" : OTHER_COUNTRY
              : DEFAULT_COUNTRY);
        const region = regionFromSaved(response?.region, country);
        const storedPhone = String(response?.phone_number ?? "").replace(/\D/g, "");
        let phoneNumber: string | undefined;
        let countryCode = country === OTHER_COUNTRY ? "" : getCountry(country).dialCode;
        originalPhoneRef.current = null;
        if (storedPhone) {
          // Legacy numbers without a recognisable code are shown as-is with
          // a best-guess code (India region -> +91).
          const split = splitDigitsPhone(storedPhone);
          countryCode =
            split?.dialCode || (response?.region === "india" ? "+91" : countryCode);
          phoneNumber = split?.number || storedPhone;
          originalPhoneRef.current = { raw: storedPhone, dialCode: countryCode, number: phoneNumber };
        }
        originalNameRef.current = response?.username ?? null;
        form.setFieldsValue({
          name: response?.username,
          email: response?.email,
          countryCode,
          phoneCountry: phoneCountryFor(countryCode, country),
          phone_number: phoneNumber,
          country,
          region,
          // Same rule the backend applies on save: Others → "Others",
          // countries without zones → the country's name. A zone not (or no
          // longer) in the country's list is left empty so a valid one is picked.
          zone:
            fixedZoneFor(country) ??
            (zonesForCountry(country, { activeOnly: false }).some(
              (z) => z.name === response?.zone
            )
              ? response.zone
              : undefined),
          district: response?.district || undefined,
          custom_district: response?.custom_district || undefined,
        });
        setSelectedDistrict(response?.district || undefined);
      }
      setLoading2(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading2(false);
    }
  };

  const submitAgent = async (body: any) => {
    const url =
      id == "create" ? API.CREATE_USER : API.PUT_UPDATE_USER + `${id}`;
    try {
      const METHOD = id == "create" ? POST : PUT;
      const response: any = await METHOD(url, body);

      if (response?.error?.status === 409) {
        const code = response.error?.details?.code;
        const existing = response.error?.details?.existing;
        let msg =
          response.error?.message || "This email or phone is already in use.";
        if (code === "UP_USER_EXISTS" && existing?.username) {
          msg = `This email or phone is already used by agent "${existing.username}".`;
        }
        message.error(msg);
        return;
      }
      if (response?.error) {
        message.error(
          response.error.message ||
            (id == "create"
              ? "Could not create agent. Please try again."
              : "Could not update agent. Please try again.")
        );
        return;
      }
      if (response?.username) {
        navigate(-1);
        message.success(
          id == "create"
            ? "Agent created successfully."
            : "Agent updated successfully."
        );
        return;
      }
      message.error(
        id == "create"
          ? "Could not create agent. Please try again."
          : "Could not update agent. Please try again."
      );
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status;
      const errBody =
        error?.data?.error ?? error?.error ?? error?.response?.data?.error;
      if (status === 409) {
        const code = errBody?.details?.code;
        const existing = errBody?.details?.existing;
        let msg =
          errBody?.message || "This email or phone is already in use.";
        if (code === "UP_USER_EXISTS" && existing?.username) {
          msg = `This email or phone is already used by agent "${existing.username}".`;
        }
        message.error(msg);
      } else {
        message.error(
          errBody?.message ||
            (id == "create"
              ? "Could not create agent. Please try again."
              : "Could not update agent. Please try again.")
        );
      }
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const normalizedEmail =
        typeof values?.email === "string"
          ? values.email.trim().toLowerCase()
          : "";
      const normalizedPhone =
        fullPhone(values?.countryCode, values?.phone_number) || null;

      const isOtherDistrict = values?.district === OTHER_DISTRICT_VALUE;
      const customDistrict = isOtherDistrict
        ? (values?.custom_district || "").trim()
        : null;

      const body: any = {
        username: cleanName(values?.name),
        email: normalizedEmail,
        password: values?.password,
        role: EPAPER_AGENT_ROLE,
        phone_number: normalizedPhone,
        screens: EPAPER_USERS_ACCESS,
        user_access: EPAPER_USERS_ACCESS.join("|"),
        // Empty → the backend uses the country's configured region.
        region: values?.region || null,
        country: values?.country || DEFAULT_COUNTRY,
        zone: values?.zone,
        district: values?.district || null,
        custom_district: customDistrict,
      };

      // When editing, password is optional — avoid sending it empty.
      if (id !== "create" && !values?.password) {
        delete body.password;
      }

      // Fresh cross-table identity check at submit time so the decision
      // modal reflects the current DB state, not stale onBlur data.
      let identity: any = null;
      try {
        const params = new URLSearchParams();
        if (normalizedEmail) params.append("email", normalizedEmail);
        if (normalizedPhone) params.append("phone", normalizedPhone);
        const checkUrl = `${API.BASE_URL}${API.IDENTITY_CHECK}?${params.toString()}`;
        const res = await axios.get(checkUrl);
        identity = res?.data || null;
      } catch (err) {
        console.error("identity check failed, continuing:", err);
      }

      const upHit = identity?.up || null;
      const epaperHit = identity?.epaper || null;
      const editingSameUser =
        id !== "create" && upHit && String(upHit.id) === String(id);

      // Case A: email/phone already belongs to another agent. Block.
      if (upHit && !editingSameUser) {
        Modal.error({
          title: "Email or phone already in use",
          content: (
            <div>
              <p>
                This email or phone is already used by agent{" "}
                <strong>{upHit.username}</strong>
                {upHit.code ? ` (${upHit.code})` : ""}.
              </p>
              <p style={{ marginTop: 8 }}>
                Please edit the existing agent instead of creating a new one.
              </p>
            </div>
          ),
        });
        return;
      }

      // Case B: only a subscriber exists. Confirm promotion.
      if (!upHit && epaperHit && id === "create") {
        const linkedToAnotherAgent =
          epaperHit.agent_id && String(epaperHit.agent_id) !== "";
        if (linkedToAnotherAgent) {
          Modal.error({
            title: "This subscriber is already linked",
            content:
              "This subscriber is already linked to an existing agent. Please unlink the current agent before reusing this email or phone.",
          });
          return;
        }

        const proceed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: "Promote this subscriber to an agent?",
            content: (
              <div>
                <p>
                  <strong>{epaperHit.full_name}</strong> is already registered
                  as a subscriber.
                </p>
                {epaperHit.email && <p>Email: {epaperHit.email}</p>}
                {epaperHit.phone && <p>Phone: {epaperHit.phone}</p>}
                <p style={{ marginTop: 12 }}>
                  Continuing will create an agent account for this person and
                  keep their existing subscription attached.
                </p>
              </div>
            ),
            okText: "Yes, promote to agent",
            cancelText: "Cancel",
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        if (!proceed) return;
      }

      // Case C (or confirmed B): submit.
      await submitAgent(body);
    } finally {
      setLoading(false);
    }
  };

  // Duplicate-username check on blur. Format errors come from the form
  // rules, so only a valid, changed name is looked up.
  const checkUser = (event: any) => {
    const username = cleanName(event.target.value);
    if (username === originalNameRef.current) return;
    if (username.length > 3 && USERNAME_PATTERN.test(username)) {
      let body = {
        email: username,
        username,
      };
      let Url = API.BASE_URL + API.GET_USERSEARCH;
      axios.post(Url, body).then((response) => {
        if (response.data.username.length) {
          setError({ name: "This Username already exists" });
        } else {
          setError("");
        }
      });
    }
  };

  // Inline email check is intentionally soft. Any cross-table conflict
  // handling (agent/admin duplicate, subscriber promotion) is decided at
  // submit time via modals + backend 409 responses — that keeps a single
  // source of truth and avoids stale onBlur state blocking the flow.
  const checkEmail = (_event: any) => {
    setError((prev: any) => {
      if (prev && typeof prev === "object" && prev.email) {
        const { email: _removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  const passwordRules = [
    { label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
    { label: "At least one lowercase letter", test: (v: string) => /[a-z]/.test(v) },
    { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
    { label: "At least one number", test: (v: string) => /\d/.test(v) },
    { label: "At least one special character", test: (v: string) => /[!@#$%^&*()_+[\]{};':"\\|,.<>?]/.test(v) },
  ];

  const allPasswordRulesPassed = passwordValue
    ? passwordRules.every((r) => r.test(passwordValue))
    : false;

  const validateName = (_: any, value: any) => {
    const name = String(value ?? "");
    if (!name.trim()) return Promise.reject(new Error("Please enter a user name"));
    if (name.trim() === originalNameRef.current) return Promise.resolve();
    if (!USERNAME_PATTERN.test(cleanName(name))) {
      return Promise.reject(
        new Error("User name can only contain letters and spaces (no numbers or symbols)")
      );
    }
    return Promise.resolve();
  };

  const validateEmail = (_: any, value: any) => {
    const email = String(value ?? "").trim();
    if (!email) return Promise.reject(new Error("Please enter an email address"));
    if (!EMAIL_PATTERN.test(email)) {
      return Promise.reject(new Error("Enter a valid email address, e.g. name@example.com"));
    }
    return Promise.resolve();
  };

  const validatePhoneNumber = ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      const local = String(value ?? "").trim();
      const dialCode: string | undefined = getFieldValue("countryCode");
      // Unchanged numbers (including legacy ones) are saved as stored.
      if (local && isOriginalPhone(dialCode, local)) return Promise.resolve();
      const result = validatePhone(getFieldValue("phoneCountry"), dialCode, local);
      if (result.valid || !result.error) return Promise.resolve();
      return Promise.reject(new Error(result.error));
    },
  });

  const validatePassword = (_: any, value: any) => {
    if (!value) return Promise.reject("Please input your password!");
    if (passwordRules.every((r) => r.test(value))) return Promise.resolve();
    return Promise.reject("");
  };

  const deleteData = (id: any) => {
    setIsLoading(true);
    let url = API.BASE_URL + API.DELETE_USER + `${id}`;
    axios
      .delete(url)
      .then(() => {
        message.success("Successfully deleted");
        setIsLoading(false);
        navigate("/Auth/agents");
      })
      .catch((err) => {
        console.log(err);
        message.error("Something went wrong...!");
        setIsLoading(false);
      });
  };

  return (
    <div>
      <PageHeader
        title={"Agents"}
        loader={loading2}
        length={`Dashboard / Agents / ${id === "create" ? "Create" : "Edit"} Agent`}
      >
        <div style={{ width: 10 }} />
        {id !== "create" && (
          <div>
            <Popconfirm
              placement="bottom"
              title={"Delete Agent"}
              description={"Are you sure you want to Delete"}
              okText="Yes"
              cancelText="No"
              onConfirm={() => deleteData(id)}
            >
              <Button loading={isLoading} danger>
                Delete Agent
              </Button>
            </Popconfirm>
          </div>
        )}
      </PageHeader>
      <Form form={form} onFinish={onFinish} name="add-agent" scrollToFirstError>
        <Container fluid>
          <Row style={{ padding: 10 }}>
            <Col lg={8} xs={12}>
              {/* Fields in aligned pairs; each row grows to its tallest field
                  so validation messages never push the other column out of line. */}
              <Row>
                <Col md={6} xs={12}>
                  <FieldLabel required>User Name</FieldLabel>
                  <Form.Item
                    name="name"
                    rules={[{ validator: validateName }]}
                    validateStatus={error["name"] ? "error" : undefined}
                    help={error["name"] || undefined}
                  >
                    <Input
                      size="large"
                      placeholder="Enter user name"
                      maxLength={50}
                      onBlur={checkUser}
                      onChange={() => {
                        // A new value clears the last duplicate-name result.
                        if (error["name"]) setError({});
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <FieldLabel>Role</FieldLabel>
                  <Form.Item>
                    <Input size="large" value="Epaper Agent" disabled />
                  </Form.Item>
                </Col>

                <Col md={6} xs={12}>
                  <FieldLabel required>Email</FieldLabel>
                  <Form.Item
                    name="email"
                    normalize={(v) => (typeof v === "string" ? v.trim() : v)}
                    rules={[{ validator: validateEmail }]}
                    validateStatus={
                      error["email"] || emailCheck.status === "taken"
                        ? "error"
                        : emailCheck.status === "checking"
                          ? "validating"
                          : emailCheck.status === "free"
                            ? "success"
                            : undefined
                    }
                    help={error["email"] || emailCheck.message || undefined}
                    hasFeedback={FEEDBACK_NO_SUCCESS_ICON}
                  >
                    <Input
                      size="large"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                      onBlur={checkEmail}
                    />
                  </Form.Item>
                </Col>
                {id === "create" && (
                  <Col md={6} xs={12}>
                    <FieldLabel required>Password</FieldLabel>
                    <Form.Item
                      name="password"
                      rules={[{ validator: validatePassword }]}
                      style={passwordValue && !allPasswordRulesPassed ? { marginBottom: 4 } : {}}
                      help={passwordValue && !allPasswordRulesPassed ? "" : undefined}
                    >
                      <Input.Password
                        size="large"
                        placeholder="Enter password"
                        onChange={(e) => setPasswordValue(e.target.value)}
                      />
                    </Form.Item>
                    {passwordValue && !allPasswordRulesPassed && (
                      <div style={{ marginBottom: 12 }}>
                        {passwordRules.map((rule, i) => {
                          const passed = rule.test(passwordValue);
                          return (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "2px 0",
                                fontSize: 13,
                                color: passed ? "#52c41a" : "#999",
                              }}
                            >
                              <span style={{ fontSize: 14 }}>{passed ? "✓" : "✕"}</span>
                              {rule.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Col>
                )}

                <Col md={6} xs={12}>
                  <FieldLabel required>Country</FieldLabel>
                  {/* Enabled countries from E-Paper Settings → Countries,
                      plus Others. Sets the phone code, region and zones. */}
                  <Form.Item
                    name="country"
                    rules={[
                      { required: true, message: "Please select a country" },
                    ]}
                  >
                    <Select
                      size="large"
                      showSearch
                      placeholder="Select Country"
                      optionFilterProp="label"
                      onChange={handleCountryChange}
                      options={countryOptions}
                    />
                  </Form.Item>
                </Col>
                {id === "create" && (
                  <Col md={6} xs={12}>
                    <FieldLabel required>Confirm Password</FieldLabel>
                    <Form.Item
                      name="confirm"
                      dependencies={["password"]}
                      rules={[
                        { required: true, message: "Please confirm your password!" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value)
                              return Promise.resolve();
                            return Promise.reject(new Error("Passwords do not match"));
                          },
                        }),
                      ]}
                      hasFeedback={FEEDBACK_NO_SUCCESS_ICON}
                    >
                      <Input.Password size="large" placeholder="Confirm password" />
                    </Form.Item>
                  </Col>
                )}

                <Col md={6} xs={12}>
                  <FieldLabel required>Phone Number</FieldLabel>
                  {/* Errors of the three inner fields show below the group;
                      the live duplicate check shows underneath them. */}
                  <Form.Item
                    // Green tick only for a valid number that is not already in use.
                    validateStatus={
                      phoneCheck.status === "taken"
                        ? "error"
                        : phoneCheck.status === "checking"
                          ? "validating"
                          : phoneResult.valid && phoneCheck.status === "free"
                            ? "success"
                            : undefined
                    }
                    extra={
                      phoneCheck.message ? (
                        <span style={phoneCheck.status === "taken" ? { color: "#ff4d4f" } : undefined}>
                          {phoneCheck.message}
                        </span>
                      ) : undefined
                    }
                    hasFeedback
                  >
                    {/* "(+91) India" country selector + number. The calling code
                        follows the country; a code box appears only for Others. */}
                    <Input.Group compact style={{ display: "flex" }}>
                      <Form.Item
                        name="phoneCountry"
                        noStyle
                        hasFeedback={false}
                        rules={[{ required: true, message: "Please select a country" }]}
                      >
                        <Select
                          size="large"
                          showSearch
                          optionFilterProp="label"
                          placeholder="Country"
                          style={{ width: isOtherPhoneCountry ? "35%" : "45%", minWidth: 120 }}
                          options={phoneCountryOptions}
                          onChange={handlePhoneCountryChange}
                        />
                      </Form.Item>
                      <Form.Item
                        name="countryCode"
                        noStyle
                        hasFeedback={false}
                        normalize={normalizeDialCode}
                        rules={[
                          {
                            validator(_, value) {
                              if (value) return Promise.resolve();
                              // Without a country the country rule already reports it.
                              if (!form.getFieldValue("phoneCountry")) return Promise.resolve();
                              return Promise.reject(new Error("Please enter the country code, e.g. +880"));
                            },
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          inputMode="tel"
                          placeholder="+code"
                          style={{
                            width: "20%",
                            minWidth: 70,
                            // Kept mounted (it holds the code) but shown only for Others.
                            display: isOtherPhoneCountry ? undefined : "none",
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="phone_number"
                        noStyle
                        dependencies={["countryCode"]}
                        // Digits only; spaces, dashes and brackets are dropped as typed.
                        normalize={(v) => String(v ?? "").replace(/\D/g, "").slice(0, 15)}
                        rules={[validatePhoneNumber]}
                      >
                        <Input
                          size="large"
                          allowClear
                          inputMode="numeric"
                          placeholder="XXXXXXXXXX"
                          style={{ width: isOtherPhoneCountry ? "45%" : "55%" }}
                        />
                      </Form.Item>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <FieldLabel>Region</FieldLabel>
                  {/* Read-only: the region set on the country in E-Paper
                      Settings → Countries (Others for Others). */}
                  <Form.Item name="region">
                    <Select
                      size="large"
                      disabled
                      placeholder="No region set for this country"
                      options={regionOptions}
                    />
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <FieldLabel required>Zone</FieldLabel>
                  <Form.Item
                    name="zone"
                    rules={[
                      { required: true, message: "Please select a zone" },
                    ]}
                  >
                    <Select
                      size="large"
                      showSearch
                      placeholder="Select Zone"
                      disabled={!hasZones}
                      allowClear={hasZones}
                      optionFilterProp="label"
                      options={hasZones ? zoneOptions : []}
                    />
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <FieldLabel>District</FieldLabel>
                  <Form.Item name="district">
                    <Select
                      size="large"
                      showSearch
                      placeholder="Select District (optional)"
                      allowClear
                      onChange={(val) => {
                        setSelectedDistrict(val);
                        if (val !== OTHER_DISTRICT_VALUE) {
                          form.setFieldsValue({ custom_district: undefined });
                        }
                      }}
                      filterOption={(input, option) => {
                        if (!option) return false;
                        const text = Array.isArray(option.children)
                          ? option.children.join("")
                          : String(option.children || "");
                        return (
                          text.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );
                      }}
                    >
                      {districts.map((d) => (
                        <Select.Option key={d} value={d}>
                          {d} ({DISTRICT_CODE_MAP[d]})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {selectedDistrict === OTHER_DISTRICT_VALUE && (
                  <Col md={6} xs={12}>
                    <FieldLabel>Custom District Name</FieldLabel>
                    <Form.Item name="custom_district">
                      <Input
                        size="large"
                        placeholder="Enter district name (optional)"
                        maxLength={100}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Col>
            <Col lg={4} xs={12}>
              <div className="userfrom-box">
                <label className="newsFormLabels" style={{ marginBottom: 0 }}>
                  Access
                </label>
                <div style={{ padding: "10px 0", color: "#555" }}>
                  E-Paper Users
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <div style={{ height: "9vh" }} />
        <div className="FixedFooter">
          <Container fluid>
            <Row>
              <Col sm={9} xs={12}></Col>
              <Col sm={1} xs={6}>
                <Button
                  size="large"
                  type="text"
                  danger
                  block
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              </Col>
              <Col sm={2} xs={6}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Form>
    </div>
  );
}

export default CreateAgent;
